// pages/PersonalData/components/DataList/DataList.tsx
import React, { useState } from 'react';
import { PersonalData } from "features/personal-data";
import { ReactComponent as IconEdit } from 'assets/img/icons/icon_Edit.svg';
import { ReactComponent as IconDelete } from 'assets/img/icons/icon_Warning.svg';
import { ReactComponent as IconSave } from 'assets/img/icons/icon_Plus.svg';
import { ReactComponent as IconCancel } from 'assets/img/icons/icon_logout.svg';
import './DataList.scss';

interface DataListProps {
  items: PersonalData[];
  type: 'email' | 'phoneNumber';
  onEdit: (id: number, value: string) => Promise<void>;
  onDelete: (id: number) => Promise<void>;
  className?: string;
}

const DataList: React.FC<DataListProps> = ({
                                             items,
                                             type,
                                             onEdit,
                                             onDelete,
                                             className = ''
                                           }) => {
  const [editingId, setEditingId] = useState<number | null>(null);
  const [editValue, setEditValue] = useState('');
  const [isLoading, setIsLoading] = useState<number | null>(null);

  const handleEdit = (item: PersonalData) => {
    setEditingId(item.id!);
    setEditValue(item.value);
  };

  const handleSave = async (id: number) => {
    setIsLoading(id);
    try {
      await onEdit(id, editValue);
      setEditingId(null);
    } catch (error) {
      console.error('Erreur lors de la modification:', error);
    } finally {
      setIsLoading(null);
    }
  };

  const handleCancel = () => {
    setEditingId(null);
    setEditValue('');
  };

  const handleDelete = async (id: number) => {
    if (window.confirm('Êtes-vous sûr de vouloir supprimer cette donnée ?')) {
      setIsLoading(id);
      try {
        await onDelete(id);
      } catch (error) {
        console.error('Erreur lors de la suppression:', error);
      } finally {
        setIsLoading(null);
      }
    }
  };

  if (items.length === 0) {
    return (
      <div className={`data-list data-list--empty ${className}`}>
        <p className="data-list__empty-text">
          Aucun {type === 'email' ? 'email' : 'téléphone'} enregistré
        </p>
      </div>
    );
  }

  return (
    <div className={`data-list ${className}`}>
      {items.map((item) => (
        <div key={item.id} className="data-list__item">
          {editingId === item.id ? (
            // Mode édition
            <div className="data-list__edit">
              <input
                type={type === 'email' ? 'email' : 'tel'}
                value={editValue}
                onChange={(e) => setEditValue(e.target.value)}
                className="data-list__input"
                placeholder={`Nouvel ${type === 'email' ? 'email' : 'téléphone'}`}
                autoFocus
              />
              <div className="data-list__actions">
                <button
                  onClick={() => handleSave(item.id!)}
                  disabled={isLoading === item.id || !editValue.trim()}
                  className="data-list__btn data-list__btn--save"
                >
                  {isLoading === item.id ? '...' : <IconSave />}
                </button>
                <button
                  onClick={handleCancel}
                  disabled={isLoading === item.id}
                  className="data-list__btn data-list__btn--cancel"
                >
                  <IconCancel />
                </button>
              </div>
            </div>
          ) : (
            // Mode affichage
            <>
              <span className="data-list__value">{item.value}</span>
              <div className="data-list__actions">
                <button
                  onClick={() => handleEdit(item)}
                  disabled={isLoading === item.id}
                  className="data-list__btn data-list__btn--edit"
                >
                  <IconEdit />
                </button>
                <button
                  onClick={() => handleDelete(item.id!)}
                  disabled={isLoading === item.id}
                  className="data-list__btn data-list__btn--delete"
                >
                  {isLoading === item.id ? '...' : <IconDelete />}
                </button>
              </div>
            </>
          )}
        </div>
      ))}
    </div>
  );
};

export default DataList;